// The admin panel's data layer (#371, docs/access-model.md slice 3).
//
// Every call here runs as the signed-in ADMIN under RLS — the 0005/0006
// policies are the lock, this module is just typed reach. Emails come
// through the `admin_list_users` security-definer RPC (auth.users is never
// client-readable) and minting through `mint_invite_codes`; everything else
// is plain table access the admin policies permit. A non-admin calling any
// of this gets refusals and empty lists from the server — the page's role
// gate is a courtesy, not the security.

import { APP_ROUTES } from '@/navigation';
import { getSupabaseRaw, profileHasAccess, profileRole } from '@/auth/session';
import type { PilotRole } from '@/auth/session';
import type { Score } from '@/music/score/types';
import { checkSavedTunes } from '@/storage/savedTunes';

export interface AdminUser {
  userId: string;
  email: string | null;
  /** The name on the account (Google's, or Apple's first-sign-in name);
   * null before migration 0019 or when none was given. */
  name: string | null;
  createdAt: string | null;
  lastSignIn: string | null;
}

export interface AdminProfile {
  userId: string;
  role: PilotRole;
  /** In the closed beta: granted by an invite code or from the People card. */
  hasAccess: boolean;
  batch: string | null;
  activatedAt: string | null;
}

export interface AdminCode {
  code: string;
  batch: string;
  useCount: number;
  maxUses: number;
  revoked: boolean;
  /** 'link': an invite link, opened at /join/<code> (migration 0016). */
  kind: 'code' | 'link';
  /** When a link stops working; null for never, and for every code. */
  expiresAt: string | null;
}

export interface AdminSetlist {
  shareId: string;
  name: string;
  owner: string;
  updatedAt: string | null;
}

/** A tune someone saved to their account (`user_tunes`, migration 0008). */
export interface AdminUserTune {
  owner: string;
  tuneId: string;
  title: string;
  composer: string;
  createdAt: string | null;
}

/** A library tune (migration 0009), with where it came from. */
export interface AdminLibraryTune {
  id: string;
  title: string;
  composer: string;
  credit: string;
  createdAt: string | null;
  sourceOwner: string | null;
  sourceTuneId: string | null;
}

export interface AdminEvent {
  userId: string;
  event: string;
  at: string;
  payload: Record<string, unknown>;
}

// --- the pure half: turning raw events into the pilot's two questions -------

export interface UserUsage {
  userId: string;
  playMinutes: number;
  events: number;
  firstSeen: string;
  lastSeen: string;
  /** Any activity a week or more after their first event — the return signal. */
  returnedAfterWeek: boolean;
}

export interface UsageSummary {
  perUser: UserUsage[];
  topTunes: { title: string; count: number }[];
  features: { feature: string; count: number }[];
  totalPlayMinutes: number;
  activeLastWeek: number;
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Aggregate a batch of events (client-side — pilot scale). Pure. */
export function summariseUsage(events: AdminEvent[], now: number = Date.now()): UsageSummary {
  const perUser = new Map<string, UserUsage>();
  const tunes = new Map<string, number>();
  const features = new Map<string, number>();
  let totalPlayMinutes = 0;

  for (const e of events) {
    const at = Date.parse(e.at);
    if (Number.isNaN(at)) continue;
    const user = perUser.get(e.userId) ?? {
      userId: e.userId,
      playMinutes: 0,
      events: 0,
      firstSeen: e.at,
      lastSeen: e.at,
      returnedAfterWeek: false,
    };
    user.events += 1;
    if (at < Date.parse(user.firstSeen)) user.firstSeen = e.at;
    if (at > Date.parse(user.lastSeen)) user.lastSeen = e.at;
    if (e.event === 'play_minutes') {
      const minutes = typeof e.payload.minutes === 'number' ? e.payload.minutes : 0;
      user.playMinutes += minutes;
      totalPlayMinutes += minutes;
    }
    if (e.event === 'tune_loaded' && typeof e.payload.title === 'string' && e.payload.title) {
      tunes.set(e.payload.title, (tunes.get(e.payload.title) ?? 0) + 1);
    }
    if (e.event === 'feature_used' && typeof e.payload.feature === 'string') {
      features.set(e.payload.feature, (features.get(e.payload.feature) ?? 0) + 1);
    }
    perUser.set(e.userId, user);
  }

  const users = [...perUser.values()];
  for (const u of users) {
    u.playMinutes = Math.round(u.playMinutes * 10) / 10;
    u.returnedAfterWeek = Date.parse(u.lastSeen) - Date.parse(u.firstSeen) >= WEEK_MS;
  }
  users.sort((a, b) => b.playMinutes - a.playMinutes || b.events - a.events);

  const byCount = (m: Map<string, number>) =>
    [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10);

  return {
    perUser: users,
    topTunes: byCount(tunes).map(([title, count]) => ({ title, count })),
    features: byCount(features).map(([feature, count]) => ({ feature, count })),
    totalPlayMinutes: Math.round(totalPlayMinutes * 10) / 10,
    activeLastWeek: users.filter((u) => now - Date.parse(u.lastSeen) <= WEEK_MS).length,
  };
}

// --- the Supabase client, structurally ---------------------------------------

type Rows = Record<string, unknown>[];
type Result = { data: Rows | null; error: { message: string } | null };
type WriteResult = { error: { message: string } | null };
/** A filtered select: awaitable, and narrowed further by another `eq`. */
type Filter = PromiseLike<Result> & { eq(column: string, value: string): Filter };

/** The slice of supabase-js the panel uses (tests fake it). */
export interface AdminClient {
  rpc(
    fn: string,
    args?: Record<string, unknown>,
  ): Promise<{ data: unknown; error: { message: string } | null }>;
  from(table: string): {
    select(columns: string): PromiseLike<Result> & {
      order(column: string, options: { ascending: boolean }): { limit(n: number): Promise<Result> };
      eq(column: string, value: string): Filter;
    };
    update(patch: Record<string, unknown>): {
      eq(column: string, value: string): Promise<WriteResult>;
    };
    delete(): { eq(column: string, value: string): Promise<WriteResult> };
  };
}

let testClient: AdminClient | null = null;

function client(): AdminClient {
  const c = testClient ?? (getSupabaseRaw() as AdminClient | null);
  if (!c) throw new Error('Sign in first.');
  return c;
}

function refuse(error: { message: string } | null): never {
  throw new Error(error?.message ?? 'The server refused.');
}

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);

export async function listUsers(): Promise<AdminUser[]> {
  const { data, error } = await client().rpc('admin_list_users');
  if (error || !Array.isArray(data)) refuse(error);
  return (data as Rows).map((r) => ({
    userId: String(r.user_id),
    email: str(r.email),
    name: str(r.name)?.trim() || null,
    createdAt: str(r.created_at),
    lastSignIn: str(r.last_sign_in_at),
  }));
}

export async function listProfiles(): Promise<AdminProfile[]> {
  const { data, error } = await client()
    .from('pilot_profiles')
    // Every column: has_access only exists from migration 0011.
    .select('*');
  if (error || !data) refuse(error);
  return data.map((r) => ({
    userId: String(r.user_id),
    role: profileRole(r),
    hasAccess: profileHasAccess(r),
    batch: str(r.batch),
    activatedAt: str(r.activated_at),
  }));
}

export async function updateProfile(
  userId: string,
  patch: { role?: PilotRole; has_access?: boolean },
): Promise<void> {
  const { error } = await client().from('pilot_profiles').update(patch).eq('user_id', userId);
  if (error) refuse(error);
}

export async function listCodes(): Promise<AdminCode[]> {
  const { data, error } = await client()
    .from('invite_codes')
    // Every column: kind and expires_at exist only from migration 0016.
    .select('*');
  if (error || !data) refuse(error);
  const codes = data.map((r) => ({
    code: String(r.code),
    batch: str(r.batch) ?? '',
    useCount: Number(r.use_count ?? 0),
    maxUses: Number(r.max_uses ?? 1),
    revoked: r.revoked === true,
    kind: r.kind === 'link' ? ('link' as const) : ('code' as const),
    expiresAt: str(r.expires_at) ?? null,
  }));
  codes.sort((a, b) => a.batch.localeCompare(b.batch) || a.code.localeCompare(b.code));
  return codes;
}

export async function mintCodes(options: { batch: string; count: number }): Promise<string[]> {
  // A code grants access and nothing else: no role (migration 0011), and no
  // flags, so grant_flags takes its default, {}.
  const { data, error } = await client().rpc('mint_invite_codes', {
    batch: options.batch,
    n: options.count,
  });
  if (error || !Array.isArray(data)) refuse(error);
  return (data as unknown[]).map(String);
}

/** Mint an invite link (migration 0016): many people, one address. */
export async function mintInviteLink(options: {
  batch: string;
  maxUses: number;
  /** null: the link never expires. */
  expiresInDays: number | null;
}): Promise<string> {
  const { data, error } = await client().rpc('mint_invite_link', {
    batch: options.batch,
    max_uses: options.maxUses,
    expires_in_days: options.expiresInDays,
  });
  if (error || typeof data !== 'string') refuse(error);
  return data as string;
}

/** The address an invite link is sent as. */
export function inviteLinkUrl(token: string, origin = window.location.origin): string {
  return `${origin}${APP_ROUTES.join}/${encodeURIComponent(token)}`;
}

export async function setCodeRevoked(code: string, revoked: boolean): Promise<void> {
  const { error } = await client().from('invite_codes').update({ revoked }).eq('code', code);
  if (error) refuse(error);
}

/** The most recent events, newest first (client-side aggregation — pilot scale). */
export async function listEvents(limit = 5000): Promise<AdminEvent[]> {
  const { data, error } = await client()
    .from('pilot_events')
    .select('user_id, event, at, payload')
    .order('at', { ascending: false })
    .limit(limit);
  if (error || !data) refuse(error);
  return data.map((r) => ({
    userId: String(r.user_id),
    event: str(r.event) ?? '',
    at: str(r.at) ?? '',
    payload:
      r.payload && typeof r.payload === 'object' ? (r.payload as Record<string, unknown>) : {},
  }));
}

/** Every SHARED playlist. The table holds every playlist of every account
 * (migration 0014); a private one is its owner's business, not the admin
 * page's. */
export async function listAllSetlists(): Promise<AdminSetlist[]> {
  const { data, error } = await client()
    .from('shared_setlists')
    .select('share_id, name, owner, link_access, updated_at');
  if (error || !data) refuse(error);
  return data
    .filter((r) => r.link_access !== 'none')
    .map((r) => ({
      shareId: String(r.share_id),
      name: str(r.name) ?? '',
      owner: String(r.owner),
      updatedAt: str(r.updated_at),
    }));
}

/** Stop a playlist being shared. It stays its owner's, private: deleting the
 * row would delete their playlist from every device of theirs. */
export async function unshareSetlist(shareId: string): Promise<void> {
  const { error } = await client()
    .from('shared_setlists')
    .update({ link_access: 'none' })
    .eq('share_id', shareId);
  if (error) refuse(error);
}

// --- New tunes and the library (step D, migrations 0008-0009) ------------------

/** The most recently saved account tunes, newest first (admins read every row). */
export async function listUserTunes(limit = 100): Promise<AdminUserTune[]> {
  const { data, error } = await client()
    .from('user_tunes')
    .select('owner, tune_id, title, composer, created_at')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error || !data) refuse(error);
  return data.map((r) => ({
    owner: String(r.owner),
    tuneId: String(r.tune_id),
    title: str(r.title) ?? '',
    composer: str(r.composer) ?? '',
    createdAt: str(r.created_at),
  }));
}

/** One saved tune's Score, to open on this screen. */
export async function fetchUserTune(owner: string, tuneId: string): Promise<Score> {
  const { data, error } = await client()
    .from('user_tunes')
    .select('tune_id, doc')
    .eq('owner', owner)
    .eq('tune_id', tuneId);
  if (error || !data) refuse(error);
  const row = data[0];
  const [score] = checkSavedTunes(
    row?.doc && typeof row.doc === 'object' ? [{ ...(row.doc as Score), id: tuneId }] : [],
  ).kept;
  if (!score) throw new Error('That tune could not be read.');
  return score;
}

/** Copy a saved tune into the library; the new library id. */
export async function promoteUserTune(input: {
  owner: string;
  tuneId: string;
  title: string;
  composer: string;
  credit: string;
}): Promise<string> {
  const { data, error } = await client().rpc('promote_user_tune', {
    p_owner: input.owner,
    p_tune_id: input.tuneId,
    p_title: input.title,
    p_composer: input.composer,
    p_credit: input.credit,
  });
  if (error || typeof data !== 'string') refuse(error);
  return data;
}

/** Every library tune with its source, newest first. */
export async function listLibrary(): Promise<AdminLibraryTune[]> {
  const c = client();
  const [tunes, sources] = await Promise.all([
    c.from('library_tunes').select('id, title, composer, credit, created_at'),
    c.from('library_tune_sources').select('library_id, source_owner, source_tune_id'),
  ]);
  if (tunes.error || !tunes.data) refuse(tunes.error);
  if (sources.error || !sources.data) refuse(sources.error);
  const sourceOf = new Map(sources.data.map((r) => [String(r.library_id), r]));
  return tunes.data
    .map((r) => {
      const source = sourceOf.get(String(r.id));
      return {
        id: String(r.id),
        title: str(r.title) ?? '',
        composer: str(r.composer) ?? '',
        credit: str(r.credit) ?? '',
        createdAt: str(r.created_at),
        sourceOwner: str(source?.source_owner),
        sourceTuneId: str(source?.source_tune_id),
      };
    })
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
}

/** Take a tune out of the library. The author's own copy is untouched. */
export async function removeLibraryTune(id: string): Promise<void> {
  const { error } = await client().from('library_tunes').delete().eq('id', id);
  if (error) refuse(error);
}

// ---------------------------------------------------------------------------
// Test seams

export function __setAdminTestHooks(hooks: { client?: AdminClient | null }): void {
  if ('client' in hooks) testClient = hooks.client ?? null;
}
